import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FaCheck } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useDebounce } from 'use-debounce';
import './index.css';

const MultiSelectDropdown = ({
  options = [],
  onSelect,
  placeholderDropdown = "Select options",
  placeholderInput = "Search...",
  showSelectAll = true,
  showSearchBar = true,
  selectedValues = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOptions, setSelectedOptions] = useState(selectedValues);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const dropdownRef = useRef(null);

  // Derived state
  const selectAll = useMemo(() => 
    selectedOptions.length === options.length && options.length > 0,
    [selectedOptions, options]
  );

  // Accessibility: Keyboard navigation
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Error handling for duplicate values
  useEffect(() => {
    const values = options.map(o => o.value);
    if (new Set(values).size !== values.length) {
      console.error('Duplicate values found in options!');
    }
  }, [options]);

  const handleSelect = (option) => {
    const newSelections = selectedOptions.some(so => so.value === option.value)
      ? selectedOptions.filter(so => so.value !== option.value)
      : [...selectedOptions, option];

    setSelectedOptions(newSelections);
    onSelect?.(newSelections);
    setSearchTerm('');
  };
  
  const handleSelectAll = (e) => {
    e.preventDefault();
    const visibleOptions = filteredOptions;
    const allSelected = visibleOptions.every(opt => 
      selectedOptions.some(so => so.value === opt.value)
    );

    const newSelections = allSelected 
      ? selectedOptions.filter(so => !visibleOptions.some(vo => vo.value === so.value))
      : [...new Set([...selectedOptions, ...visibleOptions])];

    setSelectedOptions(newSelections);
    onSelect?.(newSelections);
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  // Memoized sorted and filtered options
  const sortedOptions = useMemo(() => 
    [...options].sort((a, b) => {
      const aSelected = selectedOptions.some(so => so.value === a.value);
      const bSelected = selectedOptions.some(so => so.value === b.value);
      return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
    }),
    [options, selectedOptions]
  );

  const filteredOptions = useMemo(() =>
    sortedOptions.filter(option =>
      option.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    ),
    [sortedOptions, debouncedSearchTerm]
  );


  // Keyboard navigation handler
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => Math.max(prev - 1, 0));
        break;
        case 'Enter':
          if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
            handleSelect(filteredOptions[focusedIndex]);
          }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const maxVisibleOptions = 2;
  const visibleSelected = selectedOptions.slice(0, maxVisibleOptions);
  const additionalCount = selectedOptions.length - maxVisibleOptions;

  return (
    <div 
      className="multiSelectDropdownWrapper"
      ref={dropdownRef}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-controls="dropdown-menu"
    >
      <div
        className="multiSelectDropdownToggle"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Multi-select dropdown toggle"
      >
        <div className="selectedMultiOptions">
          {selectedOptions.length > 0 ? (
            <>
              {visibleSelected.map(option => (
                <span key={option.value} className="selectedOption">
                  {option.label}
                </span>
              ))}
              {additionalCount > 0 && (
                <span className="moreOptions">+{additionalCount}</span>
              )}
            </>
          ) : (
            <span className="placeholder">{placeholderDropdown}</span>
          )}
        </div>
        <DropdownIcon />
      </div>

      {isOpen && (
        <div 
          className="multiSelectDropdownMenu"
          role="listbox"
          aria-multiselectable="true"
          id="dropdown-menu"
        >
          <div className="searchAndSelectAll">
            {showSearchBar && (
              <div className="searchBarMultiDropdown">
                <input
                  type="text"
                  placeholder={placeholderInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="dropdownSearchInput"
                  aria-label="Search options"
                />
              </div>
            )}
            {showSelectAll && (
              <button 
              className="selectAllButton"
              onClick={handleSelectAll}
              aria-label={selectAll ? "Deselect all options" : "Select all options"}
            >
              {filteredOptions.every(opt => 
                selectedOptions.some(so => so.value === opt.value)
              ) ? 'Deselect All' : 'Select All'}
            </button>
            )}
          </div>
          <ul>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <OptionItem
                  key={option.value}
                  option={option}
                  isSelected={selectedOptions.some(so => so.value === option.value)}
                  isFocused={index === focusedIndex}
                  onSelect={handleSelect}
                  onFocus={() => setFocusedIndex(index)}
                />
              ))
            ) : (
              <li className="noOptions" role="option">No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const OptionItem = React.memo(({ option, isSelected, isFocused, onSelect, onFocus }) => (
  <li
    role="option"
    aria-selected={isSelected}
    className={`optionItem ${isSelected ? 'selected' : ''} ${isFocused ? 'focused' : ''}`}
    onClick={() => onSelect(option)}
    onMouseEnter={onFocus}
    tabIndex={-1}
  >
    {isSelected && <FaCheck className="tickIcon" />}
    {option.label}
  </li>
));

const DropdownIcon = () => (
  <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
  </svg>
);

MultiSelectDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onSelect: PropTypes.func,
  placeholderDropdown: PropTypes.string,
  placeholderInput: PropTypes.string,
  showSelectAll: PropTypes.bool,
  showSearchBar: PropTypes.bool,
};

export default MultiSelectDropdown;